import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { IconButton } from "./Button";

export type ToastTone = "info" | "success" | "error";

export interface ToastInput {
  title: string;
  description?: string;
  tone?: ToastTone;
  durationMs?: number;
  action?: {
    label: string;
    onClick: () => void | Promise<void>;
  };
}

interface ToastRecord extends ToastInput {
  id: number;
}

interface ToastContextValue {
  showToast: (toast: ToastInput) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);
let nextToastId = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<readonly ToastRecord[]>([]);
  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);
  const showToast = useCallback((input: ToastInput) => {
    nextToastId += 1;
    const toast = { ...input, id: nextToastId };
    setToasts((current) => [...current, toast]);
    window.setTimeout(
      () => dismiss(toast.id),
      input.durationMs ?? (input.tone === "error" ? 7000 : 4000),
    );
  }, [dismiss]);
  const value = useMemo(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        className="ui-toast-viewport"
        aria-label="Notifications"
        aria-live="polite"
      >
        {toasts.map((toast) => (
          <div
            key={toast.id}
            role={toast.tone === "error" ? "alert" : "status"}
            className={`ui-toast ui-toast--${toast.tone ?? "info"}`}
          >
            <span className="ui-toast__indicator" aria-hidden="true" />
            <span className="ui-toast__copy">
              <strong>{toast.title}</strong>
              {toast.description ? <small>{toast.description}</small> : null}
            </span>
            {toast.action ? (
              <button
                type="button"
                className="ui-toast__action"
                onClick={() => {
                  dismiss(toast.id);
                  void toast.action?.onClick();
                }}
              >
                {toast.action.label}
              </button>
            ) : null}
            <IconButton
              size="small"
              label="Dismiss notification"
              onClick={() => dismiss(toast.id)}
            >
              ×
            </IconButton>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const value = useContext(ToastContext);
  if (!value) {
    throw new Error("useToast must be used inside ToastProvider");
  }
  return value;
}
